import { Injectable, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PayPeriod, Shift } from './hours-tracker.model';

@Injectable({ providedIn: 'root' })
export class HoursTrackerService {
  private injector = inject(EnvironmentInjector);

  constructor(private db: AngularFireDatabase) {}

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  getPayPeriods(): Observable<PayPeriod[]> {
    return this.inCtx(() => this.db.list<Omit<PayPeriod, 'id'>>('payPeriods').snapshotChanges().pipe(
      map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as Omit<PayPeriod, 'id'>) })))
    ));
  }

  getShifts(): Observable<Shift[]> {
    return this.inCtx(() => this.db.list<Omit<Shift, 'id'>>('shifts').snapshotChanges().pipe(
      map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as Omit<Shift, 'id'>) })))
    ));
  }

  clockIn(periodId: string): Promise<string> {
    const shift: Omit<Shift, 'id'> = {
      periodId,
      clockIn: new Date().toISOString(),
      clockOut: null
    };
    return this.inCtx(() => this.db.list('shifts').push(shift)).then(ref => ref.key as string);
  }

  clockOut(shiftId: string): Promise<void> {
    return this.inCtx(() => this.db.object(`shifts/${shiftId}`).update({ clockOut: new Date().toISOString() }));
  }

  saveShift(shift: Omit<Shift, 'id'>): Promise<void> {
    return this.inCtx(() => this.db.list('shifts').push(shift)).then(() => {});
  }

  updateShift(shiftId: string, data: Partial<Omit<Shift, 'id'>>): Promise<void> {
    return this.inCtx(() => this.db.object(`shifts/${shiftId}`).update(data));
  }

  deleteShift(shiftId: string): Promise<void> {
    return this.inCtx(() => this.db.object(`shifts/${shiftId}`).remove());
  }

  addPayPeriod(period: Omit<PayPeriod, 'id'>): Promise<void> {
    return this.inCtx(() => this.db.list('payPeriods').push(period)).then(() => {});
  }

  updatePayPeriod(periodId: string, data: Partial<Omit<PayPeriod, 'id'>>): Promise<void> {
    return this.inCtx(() => this.db.object(`payPeriods/${periodId}`).update(data));
  }

  deletePayPeriod(periodId: string): Promise<void> {
    return this.inCtx(() => this.db.object(`payPeriods/${periodId}`).remove());
  }
}
